'use client';

import { useState, useCallback } from 'react';

import { ConfirmHookOptions, ConfirmHookReturn } from './types';

export const useConfirm = (): ConfirmHookReturn => {
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    options: ConfirmHookOptions | null;
    resolve: ((value: boolean) => void) | null;
  }>({
    isOpen: false,
    options: null,
    resolve: null,
  });

  const confirm = useCallback((options: ConfirmHookOptions): Promise<boolean> => {
    return new Promise(resolve => {
      setConfirmState({
        isOpen: true,
        options,
        resolve,
      });
    });
  }, []);

  const confirmModal = useCallback(
    (options: ConfirmHookOptions): Promise<boolean> => {
      // Modal confirm sẽ được implement riêng nếu cần
      return confirm(options);
    },
    [confirm]
  );

  const confirmTooltip = useCallback(
    (options: ConfirmHookOptions): Promise<boolean> => {
      // Tooltip confirm sẽ được implement riêng nếu cần
      return confirm(options);
    },
    [confirm]
  );

  const handleConfirm = useCallback(() => {
    confirmState.options?.onConfirm?.();
    confirmState.resolve?.(true);
    setConfirmState({
      isOpen: false,
      options: null,
      resolve: null,
    });
  }, [confirmState]);

  const handleCancel = useCallback(() => {
    confirmState.options?.onCancel?.();
    confirmState.resolve?.(false);
    setConfirmState({
      isOpen: false,
      options: null,
      resolve: null,
    });
  }, [confirmState]);

  return {
    confirm,
    confirmModal,
    confirmTooltip,
    // Expose internal state for custom confirm components
    confirmState: {
      ...confirmState,
      handleConfirm,
      handleCancel,
    },
  };
};

// Hook đơn giản hơn cho việc sử dụng trực tiếp
export const useConfirmAction = () => {
  const { confirm } = useConfirm();

  const confirmDelete = useCallback(
    (itemName?: string) => {
      return confirm({
        title: 'Xác nhận xóa',
        message: itemName
          ? `Bạn có chắc chắn muốn xóa "${itemName}"? Hành động này không thể hoàn tác.`
          : 'Bạn có chắc chắn muốn xóa? Hành động này không thể hoàn tác.',
        confirmText: 'Xóa',
        cancelText: 'Hủy',
        confirmVariant: 'danger',
        cancelVariant: 'secondary',
      });
    },
    [confirm]
  );

  const confirmSave = useCallback(
    (itemName?: string) => {
      return confirm({
        title: 'Xác nhận lưu',
        message: itemName
          ? `Bạn có chắc chắn muốn lưu "${itemName}"?`
          : 'Bạn có chắc chắn muốn lưu?',
        confirmText: 'Lưu',
        cancelText: 'Hủy',
        confirmVariant: 'primary',
        cancelVariant: 'secondary',
      });
    },
    [confirm]
  );

  const confirmAction = useCallback(
    (actionName: string, itemName?: string) => {
      return confirm({
        title: `Xác nhận ${actionName.toLowerCase()}`,
        message: itemName
          ? `Bạn có chắc chắn muốn ${actionName.toLowerCase()} "${itemName}"?`
          : `Bạn có chắc chắn muốn ${actionName.toLowerCase()}?`,
        confirmText: actionName,
        cancelText: 'Hủy',
        confirmVariant: 'primary',
        cancelVariant: 'secondary',
      });
    },
    [confirm]
  );

  return {
    confirmDelete,
    confirmSave,
    confirmAction,
    confirm,
  };
};
